import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VersionControlPanel = ({ onVersionSelect, currentVersion }) => {
  const [showAllVersions, setShowAllVersions] = useState(false);

  const versions = [
    {
      id: 'v1.3',
      version: '1.3',
      status: 'Current',
      author: 'SAP Architect',
      date: '2024-10-10',
      time: '01:37',
      changes: 'Updated fit/gap analysis results and added new process diagrams',
      approvals: 3,
      totalApprovers: 4,
      isCurrent: true
    },
    {
      id: 'v1.2',
      version: '1.2',
      status: 'Approved',
      author: 'Business Analyst',
      date: '2024-10-08',
      time: '14:22',
      changes: 'Incorporated stakeholder feedback on requirements section',
      approvals: 4,
      totalApprovers: 4,
      isCurrent: false
    },
    {
      id: 'v1.1',
      version: '1.1',
      status: 'Draft',
      author: 'SAP Architect',
      date: '2024-10-05',
      time: '09:15',
      changes: 'Initial draft with scope definition and basic requirements',
      approvals: 0,
      totalApprovers: 4,
      isCurrent: false
    },
    {
      id: 'v1.0',
      version: '1.0',
      status: 'Archived',
      author: 'Project Manager',
      date: '2024-10-01',
      time: '16:45',
      changes: 'Template initialization and project setup',
      approvals: 1,
      totalApprovers: 4,
      isCurrent: false
    }
  ];

  const displayedVersions = showAllVersions ? versions : versions?.slice(0, 3);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Current':
        return 'bg-blue-100 text-blue-700';
      case 'Approved':
        return 'bg-green-100 text-green-700';
      case 'Draft':
        return 'bg-yellow-100 text-yellow-700';
      case 'Archived':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Current':
        return 'CheckCircle2';
      case 'Approved':
        return 'Shield';
      case 'Draft':
        return 'Edit3';
      case 'Archived':
        return 'Archive';
      default:
        return 'FileText';
    }
  };

  const handleVersionAction = (version, action) => {
    switch (action) {
      case 'view':
        onVersionSelect(version);
        break;
      case 'restore': console.log('Restoring version:', version?.version);
        break;
      case 'compare': console.log('Comparing with version:', version?.version);
        break;
      case 'download':
        console.log('Downloading version:', version?.version);
        break;
      default:
        break;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="GitBranch" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Version History</h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
        >
          Create Version
        </Button>
      </div>
      {/* Version List */}
      <div className="divide-y divide-border">
        {displayedVersions?.map((version) => (
          <div key={version?.id} className="p-4 hover:bg-muted/50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                  <Icon 
                    name={getStatusIcon(version?.status)} 
                    size={20} 
                    className="text-muted-foreground" 
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-sm font-medium text-foreground">
                      Version {version?.version}
                    </h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(version?.status)}`}>
                      {version?.status}
                    </span>
                    {version?.isCurrent && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                    {version?.changes}
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>By {version?.author}</span>
                    <span>{version?.date} at {version?.time}</span>
                    <div className="flex items-center space-x-1">
                      <Icon name="Users" size={12} />
                      <span>{version?.approvals}/{version?.totalApprovers} approved</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleVersionAction(version, 'view')}
                >
                  <Icon name="Eye" size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleVersionAction(version, 'download')}
                >
                  <Icon name="Download" size={16} />
                </Button>
                {!version?.isCurrent && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleVersionAction(version, 'compare')}
                    >
                      <Icon name="GitCompare" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleVersionAction(version, 'restore')}
                    >
                      <Icon name="RotateCcw" size={16} />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Approval Progress */}
            <div className="mt-3 ml-13">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-xs font-medium text-foreground">Approval Progress</span>
                <div className="flex-1 bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary rounded-full h-2 transition-all duration-300"
                    style={{ width: `${(version?.approvals / version?.totalApprovers) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground">
                  {Math.round((version?.approvals / version?.totalApprovers) * 100)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Show More/Less */}
      {versions?.length > 3 && (
        <div className="p-4 border-t border-border text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAllVersions(!showAllVersions)}
            iconName={showAllVersions ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            {showAllVersions ? 'Show Less' : `Show ${versions?.length - 3} More Versions`}
          </Button>
        </div>
      )}
      {/* Version Actions */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Total versions: {versions?.length}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="GitBranch"
              iconPosition="left"
            >
              Branch
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="GitMerge"
              iconPosition="left"
            >
              Merge
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionControlPanel;